<script setup lang="ts">
import type { SiteStat } from '~/types/content'

const props = defineProps<{
  stats: SiteStat[]
}>()

interface ParsedStat extends SiteStat {
  parsed: { number: number, suffix: string } | null
}

function parseStatValue(value: string): ParsedStat['parsed'] {
  const match = value.match(/^(\d+)(.*)$/)
  if (match) return { number: parseInt(match[1]!), suffix: match[2]! }
  return null
}

const displayStats = computed<ParsedStat[]>(() =>
  props.stats.map(stat => ({ ...stat, parsed: parseStatValue(stat.value) })),
)

const sectionRef = useTemplateRef<HTMLElement>('section')
const animatedValues = ref<Record<number, number>>({})

let ctx: { revert: () => void } | null = null

onMounted(async () => {
  const { gsap } = await import('gsap')
  const { ScrollTrigger } = await import('gsap/ScrollTrigger')
  gsap.registerPlugin(ScrollTrigger)

  displayStats.value.forEach((_, i) => {
    animatedValues.value[i] = 0
  })

  await nextTick()
  const section = sectionRef.value
  if (!section) return

  ctx = gsap.context(() => {
    displayStats.value.forEach((stat, i) => {
      if (!stat.parsed) return
      const obj = { val: 0 }
      gsap.to(obj, {
        val: stat.parsed.number,
        duration: 2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          once: true,
        },
        onUpdate: () => {
          animatedValues.value[i] = Math.round(obj.val)
        },
      })
    })
  }, section)
})

onUnmounted(() => ctx?.revert())
</script>

<template>
  <section
    v-if="stats.length"
    ref="section"
    class="reveal-on-scroll border-y border-[var(--color-border)] bg-[var(--color-card-bg)] backdrop-blur-sm"
  >
    <div class="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-10 sm:px-6 md:grid-cols-4 lg:px-8">
      <div
        v-for="(stat, i) in displayStats"
        :key="stat.label"
        class="reveal-on-scroll text-center"
        :style="{ transitionDelay: `${i * 100}ms` }"
      >
        <p class="tabular-nums text-3xl font-bold text-[var(--color-amber)] sm:text-4xl">
          <template v-if="stat.parsed">
            {{ animatedValues[i] || 0 }}{{ stat.parsed.suffix }}
          </template>
          <template v-else>
            {{ stat.value }}
          </template>
        </p>
        <p class="mt-1 text-sm tracking-wide text-[var(--color-steel-blue)]">
          {{ stat.label }}
        </p>
      </div>
    </div>
  </section>
</template>
